import { c as _c } from "react/compiler-runtime";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import type * as React from "react";
import { cn } from "@/lib/utils";

// Чип по макету (компоненты `.chips-atom`, `Chips N`): пилюля r96,
// Manrope 14/18 w500 #1F1F1F.
//   обычный  — заливка #1F1F1F 3 %, обводка #1F1F1F 4 % 0,5px
//   выбранный (selected) — заливка #FA5C2E 5 %, обводка #FA5C2E 0,5px
// Размеры: sm h32 pad 0/12 (панель фильтров каталога, отзывы, категории
// поддержки, «Все / Туфли и балетки» в избранном), md h34 pad 0/16 (вкладки
// «О магазине / Доставка»), lg h38 pad 10/14 (значения в панели фильтров).
// Справа — необязательный счётчик (`count`, бейдж 16 r96 10/13 w500) и крестик
// (`onRemove`) — как у активного фильтра «Размер · 3 ×».
const chipVariants = cva("inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full border-[0.5px] text-[14px] leading-[18px] font-medium text-ds-primary outline-none transition-colors select-none focus-visible:ring-2 focus-visible:ring-ds-focus focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50", {
  variants: {
    selected: {
      false: "border-ds-field-line bg-ds-field hover:bg-ds-primary/6",
      true: "border-ds-accent bg-ds-accent/5"
    },
    size: {
      sm: "h-8 px-3",
      md: "h-[34px] px-4",
      lg: "h-[38px] px-[14px]"
    }
  },
  defaultVariants: {
    selected: false,
    size: "sm"
  }
});
type ChipProps = Omit<React.ComponentProps<"button">, "children"> & VariantProps<typeof chipVariants> & {
  children?: React.ReactNode;
  /** Счётчик справа (кол-во выбранных значений, непрочитанных). */
  count?: number;
  /** Цвет счётчика: синий #6982BA (фильтры) или красный #E1574F (непрочитанные). */
  countTone?: "info" | "alert";
  /** Показать крестик и вызвать при нажатии на него (не на сам чип). */
  onRemove?: () => void;
  /** Подпись крестика для скринридера. */
  removeLabel?: string;
  asChild?: boolean;
};
function Chip(t0) {
  const $ = _c(31);
  let children;
  let className;
  let count;
  let onRemove;
  let props;
  let selected;
  let size;
  let t1;
  let t2;
  let t3;
  let type;
  if ($[0] !== t0) {
    ({
      className,
      selected,
      size,
      count,
      countTone: t1,
      onRemove,
      removeLabel: t2,
      asChild: t3,
      children,
      type,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = count;
    $[4] = onRemove;
    $[5] = props;
    $[6] = selected;
    $[7] = size;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = type;
  } else {
    children = $[1];
    className = $[2];
    count = $[3];
    onRemove = $[4];
    props = $[5];
    selected = $[6];
    size = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    type = $[11];
  }
  const countTone = t1 === undefined ? "info" : t1;
  const removeLabel = t2 === undefined ? "\u0421\u0431\u0440\u043E\u0441\u0438\u0442\u044C" : t2;
  const asChild = t3 === undefined ? false : t3;
  const Comp = asChild ? Slot.Root : "button";
  const t4 = selected ? "" : undefined;
  const t5 = asChild ? undefined : selected ?? undefined;
  const t6 = asChild ? type : type ?? "button";
  let t7;
  if ($[12] !== className || $[13] !== selected || $[14] !== size) {
    t7 = cn(chipVariants({
      selected,
      size
    }), className);
    $[12] = className;
    $[13] = selected;
    $[14] = size;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== asChild || $[17] !== children || $[18] !== count || $[19] !== countTone || $[20] !== onRemove || $[21] !== removeLabel) {
    t8 = asChild ? children : <>{children}{count != null && count > 0 && <span className={cn("inline-flex h-4 min-w-4 items-center justify-center rounded-full px-0.5 text-[10px] leading-[13px] font-medium text-white", countTone === "alert" ? "bg-ds-error" : "bg-[#6982BA]")}>{count}</span>}{onRemove && <span role="button" tabIndex={0} aria-label={removeLabel} onClick={e => {
        e.stopPropagation();
        onRemove();
      }} onKeyDown={e_0 => {
        if (e_0.key === "Enter" || e_0.key === " ") {
          e_0.preventDefault();
          e_0.stopPropagation();
          onRemove();
        }
      }} className="-mr-1 inline-flex size-4 items-center justify-center rounded-full text-ds-primary outline-none hover:text-ds-accent focus-visible:ring-2 focus-visible:ring-ds-focus"><svg viewBox="0 0 16 16" className="size-4" fill="none" aria-hidden="true"><path d="m4.5 4.5 7 7m0-7-7 7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" /></svg></span>}</>;
    $[16] = asChild;
    $[17] = children;
    $[18] = count;
    $[19] = countTone;
    $[20] = onRemove;
    $[21] = removeLabel;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== Comp || $[24] !== props || $[25] !== t4 || $[26] !== t5 || $[27] !== t6 || $[28] !== t7 || $[29] !== t8) {
    t9 = <Comp data-slot="chip" data-selected={t4} aria-pressed={t5} type={t6} className={t7} {...props}>{t8}</Comp>;
    $[23] = Comp;
    $[24] = props;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
}
export { Chip, chipVariants };
export type { ChipProps };